import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import uniq from 'lodash/uniq';

import { AdvertisingPackageResponse, SpaceResponse } from '@/api/data-contracts';
import { floorLevels } from '@/features/spaces';
import { placementAreas } from '@/shared/constants';
import { ImageResource } from '@/shared/lib/hooks/useResourceImage';

import ShoppingCartListItem from './ShoppingCartListItem';

const ShoppingCartPackageItem: FC<{
  rowId: string;
  advertisingPackage: AdvertisingPackageResponse & { cartPrice?: number };
}> = ({ rowId, advertisingPackage }) => {
  const { t } = useTranslation();

  const imageResource = useMemo<ImageResource | null>(() => {
    const objectId = advertisingPackage.supportFiles?.[0]?.objectId;
    if (!objectId || !advertisingPackage.id) return null;
    return {
      kind: 'advertising-package-document',
      packageId: advertisingPackage.id,
      documentId: String(objectId),
    };
  }, [advertisingPackage.id, advertisingPackage.supportFiles]);

  return (
    <ShoppingCartListItem
      rowId={rowId}
      table="ADVERTISING_PACKAGES"
      imageResource={imageResource}
      title={advertisingPackage.packageName}
      price={
        advertisingPackage.cartPrice ??
        (advertisingPackage.price || advertisingPackage.pricing?.monthPricing)
      }
      exposureType={
        advertisingPackage.spaces.find((s) => s.exposureType === 'INDOOR') &&
        advertisingPackage.spaces.find((s) => s.exposureType === 'OUTDOOR')
          ? t('Indoor & Outdoor')
          : advertisingPackage.spaces.find((s) => s.exposureType === 'INDOOR')
            ? t('Indoor')
            : t('Outdoor')
      }
      placementArea={uniq(
        (advertisingPackage?.spaces ?? []).reduce(
          (acc: string[], space: SpaceResponse) => [
            ...acc,
            placementAreas.find((pa) => pa.value === space.placementArea!)?.label ?? '',
          ],
          [],
        ),
      )
        .sort()
        .join(', ')}
      floor={uniq(
        (advertisingPackage?.spaces ?? []).reduce(
          (acc: string[], space: SpaceResponse) => [
            ...acc,
            floorLevels.find((fl) => fl.value === space.floor!)?.label ?? '',
          ],
          [],
        ),
      )
        .filter(Boolean)
        .sort()
        .join(', ')}
      onClick={() => window.open(`/advertising-packages/${advertisingPackage.id}`, '_blank')}
    />
  );
};

export default ShoppingCartPackageItem;
